import type { RouteLocation } from "vue-router";

import Cookies from "js-cookie";

import { base64UrlDecode } from "./utils/str";

const ID_TOKEN_COOKIE_NAME = "lmid";

export type IdTokenClaims = {
  given_name: string;
  family_name: string;
  exp: number;
  email: string;
  picture?: string;
};

export function getAuthedUser() {
  const token = Cookies.get(ID_TOKEN_COOKIE_NAME);
  if (!token) return;
  const [_, m] = token.split(".");
  return JSON.parse(base64UrlDecode(m)) as IdTokenClaims;
}

const INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds
let refreshTimeout: ReturnType<typeof setTimeout> | null = null;

export function requireAuth(to: RouteLocation) {
  const token = Cookies.get(ID_TOKEN_COOKIE_NAME);
  const q = new URLSearchParams({ return_to: to.fullPath });

  if (!token) {
    const href = `/auth/login?${q}`;
    // force skip client side routing
    window.location.href = href;
    return false;
  }
  if (refreshTimeout == null) startRefresh();
  return true;
}

async function startRefresh() {
  await maybeRefresh();
  startRefreshLoop();
}

function startRefreshLoop() {
  if (refreshTimeout) clearTimeout(refreshTimeout);
  refreshTimeout = setTimeout(async () => {
    await maybeRefresh();
    startRefreshLoop();
  }, INTERVAL);
}

async function maybeRefresh() {
  const claims = getAuthedUser();
  const now = Math.floor(Date.now() / 1000);
  if (!claims || typeof claims.exp !== "number") return;
  // if the token is not expiring soon, don't refresh
  if (claims.exp - now >= 600) return;
  await doRefresh();
}

let inflight: Promise<void> | null = null;

function doRefresh() {
  if (inflight) return inflight;

  inflight = (async () => {
    await fetch("/auth/refresh");
  })().finally(() => {
    inflight = null;
  });

  return inflight;
}
